import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, withStyles, makeStyles } from '@material-ui/core';
import { withTranslation } from 'react-i18next';
import { XS } from '../../constants/constants';
import { NUNITO } from '../../constants/fontCss';

const styles = (theme) => ({
    linkWarningDialog: {
        '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(189,189,217,0.71)'
        },
        '& .MuiDialog-paper': {
            background: '#fff',
            borderRadius: 22,
            margin: 0,
            padding: '2.5rem 3rem',
            alignItems: 'center',
            minWidth: 400,
            maxWidth: 500,
            [theme.breakpoints.down(XS)]: {
                maxWidth: 'calc(100vw - 20px)',
                padding: '1.5rem 2rem'
            }
        },
        '& .MuiDialogTitle-root': {
            color: '#0000ff',
            fontWeight: 700,
            fontSize: '1.4rem',
            textAlign: 'center',
            padding: '0 0 1rem 0',
            margin: 0
        },
        '& .MuiDialogContent-root': {
            padding: '0 0 2rem 0',
            '& .MuiDialogContentText-root': {
                color: '#605e72',
                fontWeight: 500,
                fontSize: '1rem',
                textAlign: 'center',
                lineHeight: 1.5,
                margin: 0
            }
        },
        '& .MuiDialogActions-root': {
            padding: 0,
            justifyContent: 'center',
            gap: '1rem'
        },
        '& .cancelButton': {
            background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '2px solid #dee2e6',
            borderRadius: 30,
            color: '#6c757d',
            fontSize: '1rem',
            textTransform: 'none',
            fontWeight: 600,
            minWidth: 120,
            height: 60,
            padding: '0 30px',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
                background: 'linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)',
                borderColor: '#adb5bd',
                color: '#495057',
                transform: 'translateY(-1px)',
                boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
            }
        },
        '& .confirmButton': {
            backgroundImage: 'linear-gradient(95deg, #00c1d1, #0070c7, #00c1d1) !important',
            boxShadow: '0px 4px 20px rgba(15, 15, 137, 0.1) !important',
            borderRadius: 30,
            backgroundSize: '200%',
            transition: 'all .4s ease-in-out',
            textTransform: 'none',
            height: 60,
            padding: '0 30px',
            minWidth: 120,
            border: 'none',
            '&:before': {
                background: 'none!important'
            },
            '& .MuiButton-label': {
                fontFamily: NUNITO,
                fontSize: '1rem',
                lineHeight: '1.3rem',
                fontWeight: '700',
                color: '#fff'
            },
            '&:hover': {
                backgroundPosition: '100% 0',
                transition: 'all .4s ease-in-out',
                transform: 'translateY(-1px)',
                boxShadow: '0px 6px 25px rgba(15, 15, 137, 0.2) !important'
            }
        }
    },
    linkText: {
        color: '#0070c7',
        textDecoration: 'underline',
        '&:hover': {
            color: '#0056b3',
            textDecoration: 'underline'
        }
    },
    warningIcon: {
        fontSize: '3rem',
        color: '#ff6b6b',
        marginBottom: '1rem',
        display: 'block',
        textAlign: 'center'
    },
    urlText: {
        color: '#0000ff',
        fontWeight: 600,
        wordBreak: 'break-all',
        fontSize: '0.9rem',
        marginTop: '0.5rem'
    }
});
const useStyles = makeStyles(styles);

const LinkWithWarning = ({ href, children, currentDomain, t }) => {
    const classes = useStyles();
    const [openDialog, setOpenDialog] = useState(false);

    const isExternalLink = () => {
        try {
            const url = new URL(href);
            return url.hostname !== currentDomain;
        } catch {
            return false;
        }
    };

    const handleClick = (e) => {
        if (isExternalLink()) {
            e.preventDefault();
            setOpenDialog(true);
        }
    };

    const handleConfirm = () => {
        window.open(href, '_blank', 'noopener,noreferrer');
        setOpenDialog(false);
    };

    const handleCancel = () => {
        setOpenDialog(false);
    };

    return (
        <>
            <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleClick}
                className={classes.linkText}
            >
                {children}
            </a>

            <Dialog
                open={openDialog}
                onClose={handleCancel}
                className={classes.linkWarningDialog}
                disableBackdropClick
                disableEscapeKeyDown
            >
                <DialogTitle>
                    ⚠️ {t('linkWarning.title')}
                </DialogTitle>
                <DialogContent>
                    <div style={{ textAlign: 'center' }}>
                        {t('linkWarning.message')}
                        <div className={classes.urlText}>
                            {t('linkWarning.address', { url: href })}
                        </div>
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleCancel}
                        className={'cancelButton'}
                    >
                        {t('linkWarning.cancel')}
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        className={'confirmButton'}
                        autoFocus
                    >
                        {t('linkWarning.continue')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default withStyles(styles)(withTranslation()(LinkWithWarning));
